import React from "react";
import { Box } from "@mui/material";
import { useResponsive } from "@kivunova/kivufrontendcommon";
import { useActionBarHeight } from "./useActionBarHeight";
import InlineActionBar from "./InlineActionBar";
import ModalFooterActionBar from "./ModalFooterActionBar";

type PageActionBarAlign = "start" | "center" | "end";

export interface PageActionBarProps {
  children: React.ReactNode;
  align?: PageActionBarAlign;
  modalFooter?: boolean;
  inline?: boolean;
  fullWidth?: boolean;
  sx?: React.ComponentProps<typeof Box>["sx"];
}

// Justify content mapping
const justifyMap: Record<PageActionBarAlign, string> = {
  start: "flex-start",
  center: "center",
  end: "flex-end"
};

/**
 * PageActionBar - Main action bar component for page-level actions
 * Responsive container that handles button layout and stacking
 * Automatically stacks buttons vertically on mobile, horizontal on desktop
 *
 * Variants available via props:
 * - inline: Compact horizontal-only variant
 * - modalFooter: Modal footer variant with reversed order
 * - default: Standard page action bar
 */
const PageActionBar: React.FC<PageActionBarProps> = ({
  children,
  align = "end",
  modalFooter = false,
  inline = false,
  fullWidth = false,
  sx
}) => {
  const { isMobile } = useResponsive();
  const minH = useActionBarHeight();

  // Route to specialized variants
  if (inline) {
    return <InlineActionBar sx={sx}>{children}</InlineActionBar>;
  }

  if (modalFooter) {
    return <ModalFooterActionBar sx={sx}>{children}</ModalFooterActionBar>;
  }

  // Default variant - standard page action bar
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        flexWrap: isMobile ? "nowrap" : "wrap",
        gap: 1,
        justifyContent: isMobile ? "flex-start" : justifyMap[align],
        alignItems: isMobile ? "stretch" : "center",
        width: isMobile || fullWidth ? "100%" : "auto",
        maxWidth: isMobile ? 360 : "none",
        "& > *": {
          width: isMobile ? "100% !important" : "auto",
          minHeight: minH
        },
        ...sx
      }}
    >
      {children}
    </Box>
  );
};

export default PageActionBar;
